import React, { CSSProperties, FC } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

interface ButtonProp {
  variant: 'red' | 'black'
  size?: 'sm' | 'lg'
  path: string
  type?: 'button' | 'submit' | 'reset'
  style?: CSSProperties
  external?: boolean
}

const IButton = styled.button<{ variant: string; size: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: ${(props) =>
    props.size === 'lg' ? '0.85rem 2rem' : '0.6rem 1.4rem'};
  min-height: ${(props) => (props.size === 'lg' ? '50px' : '42px')};
  border: 1.5px solid transparent;
  border-radius: var(--radius-pill);
  background-color: ${(props) =>
    props.variant === 'red' ? props.theme.colors.red : props.theme.colors.black};
  color: white;
  font-family: var(--font-sans);
  font-size: ${(props) =>
    props.size === 'lg' ? 'var(--fs-heading-sm)' : 'var(--fs-body-sm)'};
  font-weight: 600;
  /* Sentence case, near-neutral tracking: uppercase + wide tracking is what
     made these read as heavy and dated next to the rest of the page. */
  letter-spacing: 0.01em;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease, box-shadow 0.2s ease;

  a {
    color: white;
    text-decoration: none;
  }

  &:hover {
    background-color: ${(props) =>
      props.variant === 'red' ? '#6B000D' : '#1C1E1F'};
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.16);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.colors.brightRed};
    outline-offset: 3px;
  }
`

/* Outline variant for secondary actions, matching the pill geometry. */
export const GhostButton = styled(IButton)`
  background-color: transparent;
  border-color: currentColor;
  color: ${(props) => props.theme.colors.red};

  &:hover {
    background-color: ${(props) => props.theme.colors.red};
    border-color: ${(props) => props.theme.colors.red};
    color: white;
  }
`

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`

const Button: FC<ButtonProp> = ({
  variant,
  children,
  size = 'sm',
  path,
  type,
  style,
  external = false,
}) => {
  if (external) {
    return (
      <a
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
      >
        <IButton variant={variant} size={size} type={type} style={style}>
          {children}
        </IButton>
      </a>
    )
  }
  return (
    <StyledLink to={path}>
      <IButton variant={variant} size={size} type={type} style={style}>
        {children}
      </IButton>
    </StyledLink>
  )
}

export default Button
